import { useMemo, useEffect, useState, useRef, useContext } from 'react';
import { useExperienceContent } from '../../use-server-content';
import { RibbonComponentContent } from '../../../components/sections/onboarding-global-components/onboarding-feature-ribbon/dyf-feature-ribbon';
import {
    FeatureRibbonAemResponse,
    FeatureRibbonContent,
} from '../../../components/sections/onboarding-global-components/onboarding-feature-ribbon/models/vehicle-features';
import { VehicleType } from '../../../models/experiencefragments/vehicle-type';
import { Button } from '@own/fds-react';
import AuthenticationService from '../../../services/authentication-service/authentication-service';
import { useAnalytics } from '../../use-analytics';
import {
    DyfFeatureGuideContentProps,
    useFeatureGuideContent,
} from '../../../views/dyf-landing-view/hooks/use-dyf-feature-guide-content';
import ServerContext from '../../../contexts/serverContext';
import { useFeatureRibbonData } from '../use-feature-ribbon-data/use-feature-ribbon-data';
import { VinVehicleDataDetails } from '../../../services/vehicle-data-service/vehicle-data-service.types';

const TowButton = ({ buttonLabel }: { buttonLabel: string }) => {
    const { root } = useContext(ServerContext);

    const baseRoot = root ? root.substring(0, root.length - 1) : '';

    const [fireEvents] = useAnalytics();

    return (
        <div className="feature-ribbon-towing-button">
            <Button
                label={buttonLabel}
                type={'text'}
                chevron={'right'}
                href={`${baseRoot}/towing-calculator`}
                onClick={() => {
                    fireEvents(
                        'dyf-towing-calculator-onclick',
                        undefined,
                        undefined,
                        false
                    );
                }}
            />
        </div>
    );
};

const useAuthentication = () => {
    const authService = useRef(new AuthenticationService());
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

    useEffect(() => {
        authService.current.onIsAuthenticated().then(setIsAuthenticated);
    }, []);

    return isAuthenticated;
};

const getVehicleContent = (
    featureRibbonContent: FeatureRibbonAemResponse | undefined | null,
    vehicleType: VehicleType,
    vehicleDetails: VinVehicleDataDetails | null,
    loading: boolean
): RibbonComponentContent[] => {
    const ribbonContent: RibbonComponentContent[] = [];
    if (!loading && vehicleDetails && vehicleType && featureRibbonContent) {
        const vehicleContent = featureRibbonContent[vehicleType] || [];
        let disclaimerCount = 0;

        vehicleContent.forEach((content: FeatureRibbonContent) => {
            const fieldLabel = content.fieldLabel as keyof VinVehicleDataDetails;
            const description: string = vehicleDetails[fieldLabel] || '';
            if (content.disclaimer) disclaimerCount++;
            if (description && content.fieldResponse) {
                ribbonContent.push({
                    featureTitle: content.fieldResponse,
                    featureDescription: description,
                    disclaimer: content.disclaimer,
                    disclaimerIndex: content.disclaimer
                        ? disclaimerCount
                        : null,
                });
            }
        });
    }
    return ribbonContent;
};

export const useFeatureRibbonContent = (
    vin: string,
    vehicleType: VehicleType,
    eligibleTowingCalculator: boolean
): RibbonComponentContent[] => {
    const isAuthenticated = useAuthentication();
    const [featureRibbonContent] = useExperienceContent<
        FeatureRibbonAemResponse
    >('dyf', 'feature-ribbon', 'feature_ribbon', undefined, false);
    const featureGuideContent: DyfFeatureGuideContentProps | null = useFeatureGuideContent();
    const { data: vehicleDetails, loading } = useFeatureRibbonData(vin);

    const featureRibbonData = useMemo<RibbonComponentContent[]>(() => {
        const ribbonContent = getVehicleContent(
            featureRibbonContent,
            vehicleType,
            vehicleDetails,
            loading
        );

        if (
            vin &&
            isAuthenticated &&
            eligibleTowingCalculator &&
            featureGuideContent
        ) {
            ribbonContent.push({
                featureTitle:
                    featureGuideContent?.featureRibbonTowingTitle || '',
                featureDescription: (
                    <TowButton
                        buttonLabel={
                            featureGuideContent?.featureRibbonTowingButtonLabel ??
                            ''
                        }
                    />
                ),
                disclaimer: null,
                disclaimerIndex: null,
            });
        }

        return ribbonContent;
    }, [
        featureGuideContent,
        featureRibbonContent,
        isAuthenticated,
        eligibleTowingCalculator,
        loading,
        vehicleDetails,
        vehicleType,
        vin,
    ]);

    return featureRibbonData;
};
